/**
 * Meta Ads API Service
 * Fetches campaign data from Facebook/Instagram Marketing API
 *
 * Setup:
 * 1. Create a Meta Business account and Facebook App at developers.facebook.com
 * 2. Get your Ad Account ID from Meta Business Suite
 * 3. Generate a Marketing API access token
 * 4. Add environment variables in Vercel (server-side, no REACT_APP_ prefix):
 *    - META_ACCESS_TOKEN (secret - server only)
 *    - META_AD_ACCOUNT_ID (can be public)
 *    - META_FACEBOOK_PAGE_ID (can be public)
 */

import { getConfig } from 'src/api/runtime-config';

export interface MetaCampaign {
  id: string;
  name: string;
  status: 'ACTIVE' | 'PAUSED' | 'DELETED' | 'ARCHIVED';
  objective: string;
  created_time: string;
  updated_time: string;
  start_time?: string;
  stop_time?: string;
  daily_budget?: string;
  lifetime_budget?: string;
  budget_remaining?: string;
}

export interface MetaInsights {
  campaign_id: string;
  campaign_name: string;
  impressions: string;
  reach: string;
  clicks: string;
  ctr: string;
  cpc: string;
  cpm: string;
  spend: string;
  actions?: Array<{
    action_type: string;
    value: string;
  }>;
  cost_per_action_type?: Array<{
    action_type: string;
    value: string;
  }>;
  date_start: string;
  date_stop: string;
}

export interface ProcessedCampaign {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'paused' | 'completed' | 'draft' | 'scheduled';
  type: 'facebook' | 'google' | 'email' | 'referral' | 'seasonal';
  budget: number;
  spent: number;
  impressions: number;
  clicks: number;
  leads: number;
  conversions: number;
  cpl: number;
  roas: number;
  startDate: string;
  endDate: string;
  targetAudience: string[];
  performance: 'excellent' | 'good' | 'average' | 'poor';
}

const META_API_VERSION = 'v18.0';
const META_API_BASE = `https://graph.facebook.com/${META_API_VERSION}`;

// Token is handled server-side via proxy - never exposed to client
const getAccessToken = (): string | null => {
  return null; // Only available server-side
};

// These IDs can be public - they're just identifiers, not secrets
const getAdAccountId = (): string | null => {
  return getConfig('REACT_APP_META_AD_ACCOUNT_ID') || null;
};

const getPageId = (): string | null => {
  return getConfig('REACT_APP_META_FACEBOOK_PAGE_ID') || null;
};

// Always use proxy to keep tokens secure
const shouldUseProxy = (): boolean => {
  return true; // Always use proxy for security
};

// Cache for the Page Access Token
let cachedPageAccessToken: string | null = null;

/**
 * Clear the cached Page Access Token (useful for retry)
 */
export const clearPageAccessTokenCache = () => {
  cachedPageAccessToken = null;
};

/**
 * Get Page Access Token from User Access Token
 * The Page Access Token is required for accessing lead forms and leads
 */
const getPageAccessToken = async (): Promise<string | null> => {
  if (cachedPageAccessToken) {
    // console.log('[Meta Ads] Using cached Page Access Token');
    return cachedPageAccessToken;
  }

  const userAccessToken = getAccessToken();
  // console.log('[Meta Ads] User Access Token available:', !!userAccessToken);
  // console.log('[Meta Ads] Using proxy:', shouldUseProxy());

  if (!userAccessToken && !shouldUseProxy()) {
    // console.log('[Meta Ads] No user access token available and not using proxy');
    return null;
  }

  try {
    // First, fetch the list of pages the user has access to
    // console.log('[Meta Ads] Fetching pages from me/accounts...');
    const response = await metaAdsFetch('me/accounts');

    const responseText = await response.text();
    // console.log('[Meta Ads] me/accounts response status:', response.status);
    // console.log('[Meta Ads] me/accounts response:', responseText.substring(0, 500));

    let data;
    try {
      data = JSON.parse(responseText);
    } catch {
      // console.error('[Meta Ads] Failed to parse me/accounts response');
      return null;
    }

    if (!response.ok) {
      // console.warn('[Meta Ads] Could not fetch Page Access Token:', data.error?.message);
      return null;
    }

    const pageId = getPageId();
    // console.log('[Meta Ads] Looking for Page ID:', pageId);
    // console.log('[Meta Ads] Pages available:', data.data?.length || 0);

    if (data.data?.length > 0) {
      // console.log('[Meta Ads] Available pages:', data.data.map((p: any) => ({ id: p.id, name: p.name, hasToken: !!p.access_token })));
    }

    // Find the page matching our configured Page ID
    const page = (data.data || []).find((p: any) => p.id === pageId);

    if (page?.access_token) {
      cachedPageAccessToken = page.access_token;
      // console.log('[Meta Ads] Page Access Token obtained for page:', page.name);
      return cachedPageAccessToken;
    }

    // If no specific page found, use first available page
    if (data.data?.[0]?.access_token) {
      cachedPageAccessToken = data.data[0].access_token;
      // console.log('[Meta Ads] Using first available Page Access Token for:', data.data[0].name);
      return cachedPageAccessToken;
    }

    // console.warn('[Meta Ads] No page with access token found. Make sure your token has pages_read_engagement or pages_manage_metadata permission.');
    return null;
  } catch (error) {
    // console.error('[Meta Ads] Error fetching Page Access Token:', error);
    return null;
  }
};

// Helper to safely parse JSON, throwing a clear error if response is HTML (e.g. dev server fallback)
const safeJsonParse = async (response: Response): Promise<any> => {
  const text = await response.text();
  if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
    throw new Error(
      'Meta Ads API proxy not available. Make sure the /api/proxy/meta endpoint is deployed (Vercel serverless function).'
    );
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response from Meta API: ${text.substring(0, 100)}`);
  }
};

// Helper to make Meta Ads API requests (uses proxy in production)
const metaAdsFetch = async (
  endpoint: string,
  params: Record<string, string> = {},
  options: { method?: string; body?: Record<string, unknown>; usePageToken?: boolean } = {}
): Promise<Response> => {
  const method = options.method || 'GET';

  if (shouldUseProxy()) {
    // Use serverless function proxy in production
    const url = new URL('/api/proxy/meta', window.location.origin);
    url.searchParams.append('endpoint', endpoint);
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.append(key, value);
    });

    const fetchOptions: RequestInit = { method };
    if (options.body) {
      fetchOptions.headers = { 'Content-Type': 'application/json' };
      fetchOptions.body = JSON.stringify(options.body);
    }

    return fetch(url.toString(), fetchOptions);
  } else {
    // Direct API call in development
    const accessToken = getAccessToken();
    const url = new URL(`${META_API_BASE}/${endpoint}`);
    if (accessToken) {
      url.searchParams.append('access_token', accessToken);
    }
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.append(key, value);
    });

    const fetchOptions: RequestInit = { method };
    if (options.body) {
      fetchOptions.headers = { 'Content-Type': 'application/json' };
      fetchOptions.body = JSON.stringify({ ...options.body, access_token: accessToken });
    }

    return fetch(url.toString(), fetchOptions);
  }
};

// Helper to make Meta Ads API requests with Page Access Token
const metaAdsPageFetch = async (
  endpoint: string,
  params: Record<string, string> = {},
  options: { method?: string; body?: Record<string, unknown> } = {}
): Promise<Response> => {
  const method = options.method || 'GET';

  if (shouldUseProxy()) {
    // Use serverless function proxy in production
    const url = new URL('/api/proxy/meta', window.location.origin);
    url.searchParams.append('endpoint', endpoint);
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.append(key, value);
    });

    const fetchOptions: RequestInit = { method };
    if (options.body) {
      fetchOptions.headers = { 'Content-Type': 'application/json' };
      fetchOptions.body = JSON.stringify(options.body);
    }

    return fetch(url.toString(), fetchOptions);
  } else {
    // Direct API call in development - use Page Access Token
    const pageAccessToken = await getPageAccessToken();
    if (!pageAccessToken) {
      throw new Error('Could not obtain Page Access Token. Make sure your User Access Token has pages_manage_metadata and leads_retrieval permissions.');
    }

    const url = new URL(`${META_API_BASE}/${endpoint}`);
    url.searchParams.append('access_token', pageAccessToken);
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.append(key, value);
    });

    const fetchOptions: RequestInit = { method };
    if (options.body) {
      fetchOptions.headers = { 'Content-Type': 'application/json' };
      fetchOptions.body = JSON.stringify({ ...options.body, access_token: pageAccessToken });
    }

    return fetch(url.toString(), fetchOptions);
  }
};

/**
 * Check if Meta Ads API is configured
 */
export const isMetaAdsConfigured = (): boolean => {
  // In production with proxy, we don't need the token on the client
  if (shouldUseProxy()) {
    return Boolean(getAdAccountId());
  }
  return Boolean(getAccessToken() && getAdAccountId());
};

/**
 * Fetch all campaigns from Meta Ads
 */
export const fetchMetaCampaigns = async (): Promise<MetaCampaign[]> => {
  const adAccountId = getAdAccountId();

  if (!isMetaAdsConfigured()) {
    // console.warn('Meta Ads not configured. Set REACT_APP_META_ACCESS_TOKEN and REACT_APP_META_AD_ACCOUNT_ID');
    return [];
  }

  try {
    const response = await metaAdsFetch(`act_${adAccountId}/campaigns`, {
      fields: 'id,name,status,objective,created_time,updated_time,start_time,stop_time,daily_budget,lifetime_budget,budget_remaining',
      limit: '100',
    });

    const data = await safeJsonParse(response);

    if (!response.ok) {
      const msg = data.error?.message || data.error || 'Failed to fetch campaigns';
      console.error('[Meta Ads] Campaign fetch error:', JSON.stringify(data, null, 2));
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }

    return data.data || [];
  } catch (error) {
    console.error('[Meta Ads] fetchMetaCampaigns error:', error);
    throw error;
  }
};

/**
 * Fetch campaign insights (metrics) for a date range
 */
export const fetchCampaignInsights = async (
  datePreset: 'last_7d' | 'last_30d' | 'last_90d' | 'last_year' | 'maximum' = 'last_30d'
): Promise<MetaInsights[]> => {
  const adAccountId = getAdAccountId();

  if (!isMetaAdsConfigured()) {
    return [];
  }

  try {
    const response = await metaAdsFetch(`act_${adAccountId}/insights`, {
      level: 'campaign',
      fields: 'campaign_id,campaign_name,impressions,reach,clicks,ctr,cpc,cpm,spend,actions,cost_per_action_type',
      date_preset: datePreset,
      limit: '100',
    });

    const data = await safeJsonParse(response);

    if (!response.ok) {
      const msg = data.error?.message || data.error || 'Failed to fetch insights';
      console.error('[Meta Ads] Insights fetch error:', JSON.stringify(data, null, 2));
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }

    return data.data || [];
  } catch (error) {
    console.error('[Meta Ads] fetchCampaignInsights error:', error);
    throw error;
  }
};

/**
 * Map Meta API status to our status format
 */
const mapStatus = (status: MetaCampaign['status']): ProcessedCampaign['status'] => {
  switch (status) {
    case 'ACTIVE':
      return 'active';
    case 'PAUSED':
      return 'paused';
    case 'DELETED':
    case 'ARCHIVED':
      return 'completed';
    default:
      return 'draft';
  }
};

/**
 * Calculate performance rating based on ROAS and CPL
 */
const calculatePerformance = (roas: number, cpl: number): ProcessedCampaign['performance'] => {
  if (roas >= 4 || cpl <= 15) return 'excellent';
  if (roas >= 3 || cpl <= 25) return 'good';
  if (roas >= 2 || cpl <= 35) return 'average';
  return 'poor';
};

/**
 * Extract leads from Meta actions array
 */
const extractLeads = (actions?: Array<{ action_type: string; value: string }>): number => {
  if (!actions) return 0;

  const leadAction = actions.find(a =>
    a.action_type === 'lead' ||
    a.action_type === 'onsite_conversion.lead_grouped' ||
    a.action_type === 'offsite_conversion.fb_pixel_lead'
  );

  return leadAction ? parseInt(leadAction.value, 10) : 0;
};

/**
 * Extract conversions (purchases) from Meta actions array
 */
const extractConversions = (actions?: Array<{ action_type: string; value: string }>): number => {
  if (!actions) return 0;

  const conversionAction = actions.find(a =>
    a.action_type === 'purchase' ||
    a.action_type === 'onsite_conversion.purchase' ||
    a.action_type === 'offsite_conversion.fb_pixel_purchase' ||
    a.action_type === 'omni_purchase'
  );

  return conversionAction ? parseInt(conversionAction.value, 10) : 0;
};

/**
 * Fetch and process all campaign data with insights
 */
export const fetchAllCampaignData = async (
  dateRange: '7' | '30' | '90' | '365' | 'lifetime' = '30'
): Promise<ProcessedCampaign[]> => {
  const datePresetMap: Record<string, 'last_7d' | 'last_30d' | 'last_90d' | 'last_year' | 'maximum'> = {
    '7': 'last_7d',
    '30': 'last_30d',
    '90': 'last_90d',
    '365': 'last_year',
    'lifetime': 'maximum',
  };

  try {
    // Fetch campaigns and insights in parallel
    const [campaigns, insights] = await Promise.all([
      fetchMetaCampaigns(),
      fetchCampaignInsights(datePresetMap[dateRange]),
    ]);

    // Create a map of insights by campaign ID
    const insightsMap = new Map<string, MetaInsights>();
    insights.forEach(insight => {
      insightsMap.set(insight.campaign_id, insight);
    });

    // Process and combine the data
    const processedCampaigns: ProcessedCampaign[] = campaigns.map(campaign => {
      const insight = insightsMap.get(campaign.id);

      const spent = insight ? parseFloat(insight.spend) : 0;
      const impressions = insight ? parseInt(insight.impressions, 10) : 0;
      const clicks = insight ? parseInt(insight.clicks, 10) : 0;
      const leads = extractLeads(insight?.actions);
      const conversions = extractConversions(insight?.actions);

      // Calculate budget (use lifetime if available, else daily * 30)
      const budget = campaign.lifetime_budget
        ? parseFloat(campaign.lifetime_budget) / 100
        : campaign.daily_budget
          ? (parseFloat(campaign.daily_budget) / 100) * 30
          : 0;

      // Calculate CPL (cost per lead)
      const cpl = leads > 0 ? spent / leads : 0;

      // Estimate ROAS (return on ad spend)
      // Average ticket value for Jellyfish Lighting ~$4,500
      const avgTicketValue = 4500;
      const revenue = conversions * avgTicketValue;
      const roas = spent > 0 ? revenue / spent : 0;

      const startDate = campaign.start_time
        ? new Date(campaign.start_time).toISOString().split('T')[0]
        : campaign.created_time.split('T')[0];

      const endDate = campaign.stop_time
        ? new Date(campaign.stop_time).toISOString().split('T')[0]
        : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 90 days out

      return {
        id: campaign.id,
        name: campaign.name,
        description: `${campaign.objective.replace(/_/g, ' ').toLowerCase()} campaign`,
        status: mapStatus(campaign.status),
        type: 'facebook' as const, // Meta = Facebook/Instagram
        budget,
        spent,
        impressions,
        clicks,
        leads,
        conversions,
        cpl: Math.round(cpl * 100) / 100,
        roas: Math.round(roas * 100) / 100,
        startDate,
        endDate,
        targetAudience: ['Meta Ads'], // Would need additional API call for targeting
        performance: calculatePerformance(roas, cpl),
      };
    });

    return processedCampaigns;
  } catch (error) {
    // console.error('Error fetching campaign data:', error);
    throw error;
  }
};

// ============================================
// Ad-Level Insights (for split testing)
// ============================================

export interface AdLevelInsight {
  ad_id: string;
  ad_name: string;
  adset_id: string;
  adset_name: string;
  campaign_id: string;
  campaign_name: string;
  impressions: number;
  clicks: number;
  spend: number;
  ctr: number;
  cpc: number;
  cpm: number;
  reach: number;
  frequency: number;
  leads: number;
  cpl: number;
  hookRate: number; // video_p25_watched_actions / impressions (approx 3s view)
  holdRate: number; // video_thruplay_actions / video_p25_watched_actions
}

/**
 * Fetch ad-level insights from Meta Ads API.
 * Returns per-ad metrics including hook rate and hold rate for video ads.
 */
export const fetchAdLevelInsights = async (
  dateRange: '7' | '30' | '90' | '365' | 'lifetime' = '30'
): Promise<AdLevelInsight[]> => {
  const adAccountId = getAdAccountId();

  if (!isMetaAdsConfigured()) {
    return [];
  }

  const datePresetMap: Record<string, string> = {
    '7': 'last_7d',
    '30': 'last_30d',
    '90': 'last_90d',
    '365': 'last_year',
    'lifetime': 'maximum',
  };

  try {
    const response = await metaAdsFetch(`act_${adAccountId}/insights`, {
      level: 'ad',
      fields: [
        'ad_id', 'ad_name', 'adset_id', 'adset_name',
        'campaign_id', 'campaign_name',
        'impressions', 'reach', 'clicks', 'ctr', 'cpc', 'cpm', 'spend', 'frequency',
        'actions', 'cost_per_action_type',
        'video_p25_watched_actions', 'video_thruplay_watched_actions',
      ].join(','),
      date_preset: datePresetMap[dateRange],
      limit: '500',
    });

    const data = await safeJsonParse(response);

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to fetch ad insights');
    }

    const rawAds = data.data || [];

    return rawAds.map((ad: any) => {
      const impressions = parseInt(ad.impressions || '0', 10);
      const clicks = parseInt(ad.clicks || '0', 10);
      const spend = parseFloat(ad.spend || '0');
      const reach = parseInt(ad.reach || '0', 10);
      const frequency = parseFloat(ad.frequency || '0');
      const ctr = parseFloat(ad.ctr || '0');
      const cpc = parseFloat(ad.cpc || '0');
      const cpm = parseFloat(ad.cpm || '0');

      const leads = extractLeads(ad.actions);
      const cpl = leads > 0 ? spend / leads : 0;

      // Hook rate: 3-second video views / impressions
      const videoP25 = (ad.video_p25_watched_actions || [])
        .reduce((sum: number, a: any) => sum + parseInt(a.value || '0', 10), 0);
      const hookRate = impressions > 0 ? (videoP25 / impressions) * 100 : 0;

      // Hold rate: ThruPlay / 3-second views
      const thruplay = (ad.video_thruplay_watched_actions || [])
        .reduce((sum: number, a: any) => sum + parseInt(a.value || '0', 10), 0);
      const holdRate = videoP25 > 0 ? (thruplay / videoP25) * 100 : 0;

      return {
        ad_id: ad.ad_id,
        ad_name: ad.ad_name || '',
        adset_id: ad.adset_id || '',
        adset_name: ad.adset_name || '',
        campaign_id: ad.campaign_id,
        campaign_name: ad.campaign_name || '',
        impressions,
        clicks,
        spend,
        ctr: Math.round(ctr * 100) / 100,
        cpc: Math.round(cpc * 100) / 100,
        cpm: Math.round(cpm * 100) / 100,
        reach,
        frequency: Math.round(frequency * 100) / 100,
        leads,
        cpl: Math.round(cpl * 100) / 100,
        hookRate: Math.round(hookRate * 100) / 100,
        holdRate: Math.round(holdRate * 100) / 100,
      };
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Get ad account info for display
 */
export const fetchAdAccountInfo = async (): Promise<{ name: string; currency: string } | null> => {
  const adAccountId = getAdAccountId();

  if (!isMetaAdsConfigured()) {
    return null;
  }

  try {
    const response = await metaAdsFetch(`act_${adAccountId}`, {
      fields: 'name,currency',
    });

    const data = await safeJsonParse(response);

    if (!response.ok) {
      return null;
    }

    return data;
  } catch {
    return null;
  }
};

// ============================================
// Campaign Creation Types & API
// ============================================

export type CampaignObjective =
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES'
  | 'OUTCOME_TRAFFIC'
  | 'OUTCOME_APP_PROMOTION';

export type BudgetType = 'daily' | 'lifetime';

export type PlacementType = 'automatic' | 'manual';

export interface MediaFile {
  id: string;
  name: string;
  type: 'image' | 'video';
  url: string;
  file?: File;
  size?: number;
  thumbnailUrl?: string;
}

export interface CampaignDraft {
  id?: string;
  // Step 1: Campaign Setup
  name: string;
  objective: CampaignObjective;
  specialAdCategories: string[];

  // Step 2: Budget & Schedule
  budgetType: BudgetType;
  budgetAmount: number;
  startDate: string;
  endDate?: string;

  // Step 3: Audience
  targetLocations: string[];
  targetAgeMin: number;
  targetAgeMax: number;
  targetGenders: ('male' | 'female' | 'all')[];
  targetInterests: string[];
  customAudiences: string[];

  // Step 4: Placements
  placementType: PlacementType;
  placements: {
    facebook: boolean;
    instagram: boolean;
    messenger: boolean;
    audienceNetwork: boolean;
  };

  // Step 5: Ad Creative (simplified)
  adFormat: 'single_image' | 'carousel' | 'video';
  primaryText: string;
  headline: string;
  description?: string;
  callToAction: string;
  websiteUrl: string;

  // Media uploads
  mediaFiles: MediaFile[];
  videoFile?: MediaFile;

  // Meta
  status: 'draft' | 'ready' | 'published';
  createdAt: string;
  updatedAt: string;
}

export const CAMPAIGN_OBJECTIVES: { value: CampaignObjective; label: string; description: string; icon: string }[] = [
  {
    value: 'OUTCOME_AWARENESS',
    label: 'Awareness',
    description: 'Reach people who are most likely to remember your ads',
    icon: 'visibility'
  },
  {
    value: 'OUTCOME_TRAFFIC',
    label: 'Traffic',
    description: 'Send people to your website or app',
    icon: 'open_in_new'
  },
  {
    value: 'OUTCOME_ENGAGEMENT',
    label: 'Engagement',
    description: 'Get more messages, video views, post engagement, or event responses',
    icon: 'thumb_up'
  },
  {
    value: 'OUTCOME_LEADS',
    label: 'Leads',
    description: 'Collect leads for your business with forms, calls, or messages',
    icon: 'contact_mail'
  },
  {
    value: 'OUTCOME_SALES',
    label: 'Sales',
    description: 'Find people likely to purchase your product or service',
    icon: 'shopping_cart'
  },
  {
    value: 'OUTCOME_APP_PROMOTION',
    label: 'App Promotion',
    description: 'Get people to install or take action in your app',
    icon: 'smartphone'
  },
];

export const CALL_TO_ACTION_OPTIONS = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'BOOK_NOW', label: 'Book Now' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'REQUEST_TIME', label: 'Request Time' },
  { value: 'CALL_NOW', label: 'Call Now' },
  { value: 'SEND_MESSAGE', label: 'Send Message' },
  { value: 'WATCH_MORE', label: 'Watch More' },
];

export const TARGET_INTERESTS_SUGGESTIONS = [
  'Home improvement',
  'Interior design',
  'Outdoor lighting',
  'Landscape design',
  'Smart home',
  'Home automation',
  'Christmas lights',
  'Holiday decorations',
  'Homeowners',
  'DIY',
  'Home renovation',
  'Luxury homes',
  'Real estate',
  'Architecture',
  'LED lighting',
];

/**
 * Create a new campaign on Meta Ads
 */
export const createMetaCampaign = async (draft: CampaignDraft): Promise<{ id: string; success: boolean }> => {
  const adAccountId = getAdAccountId();

  if (!isMetaAdsConfigured()) {
    throw new Error('Meta Ads not configured. Please add your API credentials.');
  }

  try {
    // Step 1: Create the campaign
    const campaignResponse = await metaAdsFetch(
      `act_${adAccountId}/campaigns`,
      {},
      {
        method: 'POST',
        body: {
          name: draft.name,
          objective: draft.objective,
          status: 'PAUSED', // Start paused for review
          special_ad_categories: draft.specialAdCategories.length > 0 ? draft.specialAdCategories : ['NONE'],
        },
      }
    );

    const campaignData = await safeJsonParse(campaignResponse);

    if (!campaignResponse.ok) {
      throw new Error(campaignData.error?.message || 'Failed to create campaign');
    }
    return { id: campaignData.id, success: true };
  } catch (error) {
    // console.error('Error creating Meta campaign:', error);
    throw error;
  }
};

/**
 * Update campaign status (pause/activate)
 */
export const updateCampaignStatus = async (
  campaignId: string,
  status: 'ACTIVE' | 'PAUSED'
): Promise<boolean> => {
  if (!isMetaAdsConfigured()) {
    throw new Error('Meta Ads not configured');
  }

  try {
    const response = await metaAdsFetch(
      campaignId,
      {},
      {
        method: 'POST',
        body: { status },
      }
    );

    return response.ok;
  } catch (error) {
    // console.error('Error updating campaign status:', error);
    throw error;
  }
};

/**
 * Delete a campaign
 */
export const deleteCampaign = async (campaignId: string): Promise<boolean> => {
  if (!isMetaAdsConfigured()) {
    throw new Error('Meta Ads not configured');
  }

  try {
    const response = await metaAdsFetch(
      campaignId,
      {},
      { method: 'DELETE' }
    );

    return response.ok;
  } catch (error) {
    // console.error('Error deleting campaign:', error);
    throw error;
  }
};

/**
 * Get default campaign draft
 */
export const getDefaultCampaignDraft = (): CampaignDraft => ({
  name: '',
  objective: 'OUTCOME_LEADS',
  specialAdCategories: [],
  budgetType: 'daily',
  budgetAmount: 20,
  startDate: new Date().toISOString().split('T')[0],
  endDate: undefined,
  targetLocations: ['Washington, USA'],
  targetAgeMin: 25,
  targetAgeMax: 65,
  targetGenders: ['all'],
  targetInterests: [],
  customAudiences: [],
  placementType: 'automatic',
  placements: {
    facebook: true,
    instagram: true,
    messenger: false,
    audienceNetwork: false,
  },
  adFormat: 'single_image',
  primaryText: '',
  headline: '',
  description: '',
  callToAction: 'GET_QUOTE',
  websiteUrl: 'https://www.relightexteriorlighting.com',
  mediaFiles: [],
  videoFile: undefined,
  status: 'draft',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

// ============================================
// Facebook Lead Ads API - Lead Fetching
// ============================================

/**
 * Raw lead data from Facebook Lead Ads API
 */
export interface FacebookLeadData {
  id: string;
  created_time: string;
  field_data: Array<{
    name: string;
    values: string[];
  }>;
  ad_id?: string;
  ad_name?: string;
  adset_id?: string;
  adset_name?: string;
  campaign_id?: string;
  campaign_name?: string;
  form_id?: string;
  is_organic?: boolean;
  platform?: string;
}

/**
 * Processed lead ready for import to our system
 */
export interface ProcessedFacebookLead {
  facebookLeadId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city?: string;
  state?: string;
  zipCode?: string;
  address?: string;
  createdTime: string;
  campaignName?: string;
  adName?: string;
  formId?: string;
  isOrganic: boolean;
  // Raw field data for any custom fields
  rawFields: Record<string, string>;
}

/**
 * Lead form info from Facebook
 */
export interface FacebookLeadForm {
  id: string;
  name: string;
  status: 'ACTIVE' | 'DELETED' | 'ARCHIVED';
  leads_count?: number;
  created_time?: string;
}

/**
 * Fetch all lead forms associated with the ad account's pages
 * Note: This endpoint requires a Page Access Token, not a User Access Token
 */
export const fetchLeadForms = async (): Promise<FacebookLeadForm[]> => {
  if (!isMetaAdsConfigured()) {
    // console.warn('[Facebook Leads] Meta Ads not configured');
    return [];
  }

  try {
    // Try using the Facebook Page ID from env
    const pageId = getPageId();
    // console.log('[Facebook Leads] Page ID:', pageId);

    if (!pageId) {
      // console.warn('[Facebook Leads] No REACT_APP_META_FACEBOOK_PAGE_ID set');
      return [];
    }

    // console.log('[Facebook Leads] Fetching lead forms from page (using Page Access Token)...');

    // Use metaAdsPageFetch which uses the Page Access Token
    const formsResponse = await metaAdsPageFetch(`${pageId}/leadgen_forms`, {
      fields: 'id,name,status,leads_count,created_time',
      limit: '100',
    });

    const responseData = await safeJsonParse(formsResponse);

    if (formsResponse.ok) {
      // console.log('[Facebook Leads] Fetched lead forms:', responseData.data?.length || 0);
      if (responseData.data?.length > 0) {
        // Log each form with its leads_count
        responseData.data.forEach((form: any) => {
          // console.log(`[Facebook Leads] Form: "${form.name}" (${form.id}) - Status: ${form.status}, Leads: ${form.leads_count ?? 'N/A'}`);
        });
      }
      return responseData.data || [];
    } else {
      // console.error('[Facebook Leads] API Error:', responseData.error);
      // If no lead forms exist, the API might return an empty data array
      if (responseData.data) {
        return responseData.data;
      }
      throw new Error(responseData.error?.message || 'Failed to fetch lead forms');
    }
  } catch (error) {
    // console.error('[Facebook Leads] Error fetching lead forms:', error);
    throw error; // Re-throw so the caller knows there was an error
  }
};

/**
 * Fetch leads from a specific lead form with pagination support
 * Note: This endpoint requires a Page Access Token, not a User Access Token
 */
export const fetchLeadsFromForm = async (
  formId: string,
  limit: number = 500
): Promise<FacebookLeadData[]> => {
  if (!isMetaAdsConfigured()) {
    return [];
  }

  const allLeads: FacebookLeadData[] = [];
  let nextUrl: string | null = null;
  const pageSize = Math.min(limit, 100); // Facebook max is 100 per page

  try {
    // First request
    const response = await metaAdsPageFetch(`${formId}/leads`, {
      fields: 'id,created_time,field_data,ad_id,ad_name,adset_id,adset_name,campaign_id,campaign_name,form_id,is_organic,platform',
      limit: pageSize.toString(),
    });

    const data = await safeJsonParse(response);

    if (!response.ok) {
      return [];
    }

    allLeads.push(...(data.data || []));
    nextUrl = data.paging?.next || null;

    // console.log(`[Facebook Leads] First page: ${data.data?.length || 0} leads, hasNextPage: ${!!nextUrl}`);

    // Paginate through all results until we hit the limit
    while (nextUrl && allLeads.length < limit) {
      // console.log(`[Facebook Leads] Fetching next page... (${allLeads.length} leads so far)`);

      const nextResponse = await fetch(nextUrl);
      const nextData = await nextResponse.json();

      if (!nextResponse.ok) {
        // console.warn('[Facebook Leads] Error fetching next page:', nextData.error?.message);
        break;
      }

      allLeads.push(...(nextData.data || []));
      nextUrl = nextData.paging?.next || null;
    }

    // console.log(`[Facebook Leads] Fetched ${allLeads.length} total leads from form ${formId}`);
    return allLeads.slice(0, limit);
  } catch (error) {
    // console.error('Error fetching leads from form:', error);
    // Don't throw - return empty array so we can continue with other forms
    return [];
  }
};

/**
 * Fetch all leads from all lead forms
 * @param daysBack - Number of days to look back (0 = no date filter, fetch all leads)
 * @param limit - Maximum number of leads to fetch per form
 */
export const fetchAllFacebookLeads = async (
  daysBack: number = 0, // Default to 0 (no filter) to get all leads
  limit: number = 1000
): Promise<FacebookLeadData[]> => {
  if (!isMetaAdsConfigured()) {
    // console.log('[Facebook Leads] Meta Ads not configured');
    return [];
  }

  // console.log(`[Facebook Leads] Fetching leads${daysBack > 0 ? ` from last ${daysBack} days` : ' (all time)'}...`);

  try {
    // Calculate date filter (only if daysBack > 0)
    let sinceDate: Date | null = null;
    if (daysBack > 0) {
      sinceDate = new Date();
      sinceDate.setDate(sinceDate.getDate() - daysBack);
    }

    // Fetch from all lead forms
    let forms: FacebookLeadForm[];
    try {
      forms = await fetchLeadForms();
    } catch (formError) {
      // If we can't fetch forms, throw with helpful message
      const msg = formError instanceof Error ? formError.message : 'Unknown error';
      throw new Error(`Could not fetch lead forms: ${msg}. Make sure your access token has the 'leads_retrieval' permission and you have Lead Forms in Facebook Ads Manager.`);
    }

    if (forms.length === 0) {
      // console.log('[Facebook Leads] No lead forms found on this page. Create a Lead Form in Facebook Ads Manager first.');
      return [];
    }

    // console.log(`[Facebook Leads] Found ${forms.length} lead form(s)`);
    const allLeads: FacebookLeadData[] = [];

    for (const form of forms) {
      try {
        // console.log(`[Facebook Leads] Fetching leads from form: ${form.name} (${form.id}) - reported count: ${form.leads_count || 'unknown'}`);
        // Fetch all leads from this form
        const formLeads = await fetchLeadsFromForm(form.id, limit);
        // console.log(`[Facebook Leads] Raw leads from form: ${formLeads.length}`);

        // Only filter by date if daysBack > 0
        if (sinceDate) {
          const filteredLeads = formLeads.filter(lead => {
            const leadDate = new Date(lead.created_time);
            return leadDate >= sinceDate!;
          });
          allLeads.push(...filteredLeads);
          // console.log(`[Facebook Leads] Form "${form.name}": ${filteredLeads.length} leads (after date filter)`);
        } else {
          // No date filter - add all leads
          allLeads.push(...formLeads);
          // console.log(`[Facebook Leads] Form "${form.name}": ${formLeads.length} leads (no date filter)`);
        }
      } catch (e) {
        // console.warn(`[Facebook Leads] Could not fetch leads from form ${form.id}:`, e);
      }
    }

    // console.log(`[Facebook Leads] Total: ${allLeads.length} leads from ${forms.length} forms`);
    return allLeads;
  } catch (error) {
    // console.error('[Facebook Leads] Error fetching leads:', error);
    throw error;
  }
};

/**
 * Parse field data from Facebook lead into a key-value map
 */
const parseFieldData = (fieldData: FacebookLeadData['field_data']): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const field of fieldData) {
    // Normalize field name (Facebook uses various formats)
    const key = field.name.toLowerCase().replace(/[_\s-]+/g, '_');
    result[key] = field.values?.[0] || '';
  }

  return result;
};

/**
 * Process raw Facebook leads into our format
 */
export const processFacebookLeads = (rawLeads: FacebookLeadData[]): ProcessedFacebookLead[] => {
  return rawLeads.map(lead => {
    const fields = parseFieldData(lead.field_data);

    // Try to extract name (Facebook uses various field names)
    let firstName = fields.first_name || fields.firstname || fields.full_name?.split(' ')[0] || '';
    let lastName = fields.last_name || fields.lastname || '';

    // If only full_name exists, split it
    if (!firstName && !lastName && fields.full_name) {
      const parts = fields.full_name.trim().split(' ');
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ') || '';
    }

    // Extract phone (various formats)
    const phone = fields.phone_number || fields.phone || fields.mobile || fields.cell || '';

    // Extract email
    const email = fields.email || fields.email_address || '';

    // Extract address fields
    const city = fields.city || fields.location_city || '';
    const state = fields.state || fields.location_state || fields.region || '';
    const zipCode = fields.zip || fields.zipcode || fields.zip_code || fields.postal_code || '';
    const address = fields.street_address || fields.address || fields.street || '';

    return {
      facebookLeadId: lead.id,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      city: city.trim() || undefined,
      state: state.trim() || undefined,
      zipCode: zipCode.trim() || undefined,
      address: address.trim() || undefined,
      createdTime: lead.created_time,
      campaignName: lead.campaign_name,
      adName: lead.ad_name,
      formId: lead.form_id,
      isOrganic: lead.is_organic || false,
      rawFields: fields,
    };
  });
};

/**
 * Import Facebook leads into our leads system
 * Returns the number of new leads imported and any duplicates skipped
 */
export const importFacebookLeadsToSystem = async (
  leads: ProcessedFacebookLead[],
  leadsApi: {
    create: (data: any) => Promise<any>;
    search: (query: string) => Promise<any[]>;
  }
): Promise<{ imported: number; duplicates: number; errors: string[] }> => {
  const result = { imported: 0, duplicates: 0, errors: [] as string[] };

  for (const lead of leads) {
    try {
      // Check for duplicates by email or phone
      let isDuplicate = false;

      if (lead.email) {
        const emailMatches = await leadsApi.search(lead.email);
        if (emailMatches.length > 0) {
          isDuplicate = true;
        }
      }

      if (!isDuplicate && lead.phone) {
        // Normalize phone for search
        const normalizedPhone = lead.phone.replace(/\D/g, '').slice(-10);
        if (normalizedPhone.length >= 7) {
          const phoneMatches = await leadsApi.search(normalizedPhone);
          if (phoneMatches.length > 0) {
            isDuplicate = true;
          }
        }
      }

      if (isDuplicate) {
        result.duplicates++;
        continue;
      }

      // Create the lead
      await leadsApi.create({
        firstName: lead.firstName || 'Facebook',
        lastName: lead.lastName || 'Lead',
        email: lead.email || undefined,
        phone: lead.phone || undefined,
        city: lead.city,
        state: lead.state || 'WA',
        zipCode: lead.zipCode,
        address1: lead.address,
        leadStatus: 'facebook_new',
        leadSource: 'facebook',
        facebookLeadDate: lead.createdTime,
        notes: lead.campaignName
          ? `From campaign: ${lead.campaignName}${lead.adName ? ` - Ad: ${lead.adName}` : ''}`
          : 'Imported from Facebook Lead Ads',
      });

      result.imported++;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      result.errors.push(`Failed to import ${lead.firstName} ${lead.lastName}: ${errorMsg}`);
    }
  }

  return result;
};

/**
 * Fetch leads directly from Ad Account (alternative method)
 * This can fetch leads that might not be accessible through the Page endpoint
 */
export const fetchLeadsFromAdAccount = async (
  limit: number = 500
): Promise<FacebookLeadData[]> => {
  const adAccountId = getAdAccountId();

  if (!isMetaAdsConfigured()) {
    return [];
  }

  const allLeads: FacebookLeadData[] = [];
  let nextUrl: string | null = null;
  const pageSize = Math.min(limit, 100);

  try {
    // console.log(`[Facebook Leads] Trying Ad Account endpoint for leads...`);

    // Try fetching leads through the ad account
    const response = await metaAdsFetch(`act_${adAccountId}/leads`, {
      fields: 'id,created_time,field_data,ad_id,ad_name,adset_id,adset_name,campaign_id,campaign_name,form_id,is_organic,platform',
      limit: pageSize.toString(),
    });

    const data = await safeJsonParse(response);

    if (!response.ok) {
      return [];
    }

    allLeads.push(...(data.data || []));
    nextUrl = data.paging?.next || null;

    // console.log(`[Facebook Leads] Ad Account first page: ${data.data?.length || 0} leads`);

    // Paginate
    while (nextUrl && allLeads.length < limit) {
      // console.log(`[Facebook Leads] Fetching next page from Ad Account... (${allLeads.length} leads so far)`);

      const nextResponse = await fetch(nextUrl);
      const nextData = await nextResponse.json();

      if (!nextResponse.ok) {
        // console.warn('[Facebook Leads] Error fetching next page:', nextData.error?.message);
        break;
      }

      allLeads.push(...(nextData.data || []));
      nextUrl = nextData.paging?.next || null;
    }

    // console.log(`[Facebook Leads] Fetched ${allLeads.length} total leads from Ad Account`);
    return allLeads.slice(0, limit);
  } catch (error) {
    // console.log('[Facebook Leads] Ad Account leads endpoint failed:', error);
    return [];
  }
};

/**
 * Full sync: Fetch leads from Facebook and import to our system
 */
export const syncFacebookLeads = async (
  leadsApi: {
    create: (data: any) => Promise<any>;
    search: (query: string) => Promise<any[]>;
  },
  options: {
    daysBack?: number;
    limit?: number;
  } = {}
): Promise<{ imported: number; duplicates: number; errors: string[]; total: number }> => {
  const { daysBack = 0, limit = 1000 } = options;

  // Clear any cached Page Access Token to ensure fresh fetch
  clearPageAccessTokenCache();

  // Try both methods to get leads
  // console.log('[Facebook Leads] Starting sync...');

  // Method 1: Fetch from lead forms via Page
  const pageLeads = await fetchAllFacebookLeads(daysBack, limit);
  // console.log(`[Facebook Leads] Got ${pageLeads.length} leads from Page/Forms`);

  // Method 2: Try fetching from Ad Account directly
  const adAccountLeads = await fetchLeadsFromAdAccount(limit);
  // console.log(`[Facebook Leads] Got ${adAccountLeads.length} leads from Ad Account`);

  // Combine and deduplicate by lead ID
  const leadMap = new Map<string, FacebookLeadData>();
  for (const lead of pageLeads) {
    leadMap.set(lead.id, lead);
  }
  for (const lead of adAccountLeads) {
    if (!leadMap.has(lead.id)) {
      leadMap.set(lead.id, lead);
    }
  }

  const rawLeads = Array.from(leadMap.values());
  // console.log(`[Facebook Leads] Combined total: ${rawLeads.length} unique leads`);

  const processedLeads = processFacebookLeads(rawLeads);

  // console.log(`[Facebook Leads] Processing ${processedLeads.length} leads...`);

  // Import to our system
  const importResult = await importFacebookLeadsToSystem(processedLeads, leadsApi);

  return {
    ...importResult,
    total: processedLeads.length,
  };
};

// ============================================
// Ads with Creative Data (for Advertisements tab)
// ============================================

export interface AdWithCreative {
  ad_id: string;
  ad_name: string;
  campaign_name: string;
  campaign_id: string;
  adset_name: string;
  status: string;
  // Creative data
  thumbnail_url: string | null;
  image_url: string | null;        // Full-resolution image
  video_url: string | null;        // Facebook watch URL
  video_source: string | null;     // Direct MP4 source URL for <video> element
  video_thumbnail: string | null;  // Video poster/thumbnail
  body: string | null;
  title: string | null;
  link_url: string | null;
  call_to_action_type: string | null;
  is_video: boolean;
  // Targeting (from adset)
  age_min: number | null;
  age_max: number | null;
  geo_locations: string[];
  targeting_tags: string[];
  // Metrics (merged from insights)
  impressions: number;
  clicks: number;
  spend: number;
  leads: number;
  cpl: number;
  ctr: number;
}

/**
 * Fetch ads with creative data and targeting info, merged with metrics.
 * Returns visual ad card data for the Advertisements tab.
 *
 * Strategy: Use batch endpoints (act_{id}/adcreatives, act_{id}/advideos)
 * instead of individual node lookups, which are more reliable and faster.
 */
export const fetchAdsWithCreatives = async (
  dateRange: '7' | '30' | '90' | '365' | 'lifetime' = '30'
): Promise<AdWithCreative[]> => {
  const adAccountId = getAdAccountId();

  if (!isMetaAdsConfigured()) {
    return [];
  }

  try {
    // Step 1: Fetch ads with basic creative info + adset targeting
    // Only request safe sub-fields on the ads edge (no effective_image_url or video_id here)
    const adsResponse = await metaAdsFetch(`act_${adAccountId}/ads`, {
      fields: [
        'id', 'name', 'status',
        'campaign{id,name}',
        'adset{id,name,targeting}',
        'creative{id}',
      ].join(','),
      limit: '200',
    });

    const adsData = await safeJsonParse(adsResponse);
    if (!adsResponse.ok) {
      throw new Error(adsData.error?.message || 'Failed to fetch ads');
    }

    const rawAds = adsData.data || [];
    console.log(`[Meta Ads] Step 1: Fetched ${rawAds.length} ads`);

    // Step 2: Fetch ALL ad creatives from the ad account in one batch call
    // This is much more reliable than individual creative ID lookups
    const creativeMap = new Map<string, any>();
    try {
      let pageNum = 0;

      // First page
      const creativesResponse = await metaAdsFetch(`act_${adAccountId}/adcreatives`, {
        fields: [
          'id', 'name', 'body', 'title', 'link_url', 'call_to_action_type',
          'thumbnail_url', 'image_url', 'effective_image_url',
          'video_id', 'object_story_spec',
        ].join(','),
        limit: '200',
      });

      const creativesData = await safeJsonParse(creativesResponse);
      if (creativesResponse.ok && creativesData.data) {
        for (const creative of creativesData.data) {
          creativeMap.set(String(creative.id), creative);
        }
        pageNum++;

        // Follow pagination
        let nextCursor = creativesData.paging?.cursors?.after;
        while (nextCursor && pageNum < 5) {
          const nextResp = await metaAdsFetch(`act_${adAccountId}/adcreatives`, {
            fields: [
              'id', 'name', 'body', 'title', 'link_url', 'call_to_action_type',
              'thumbnail_url', 'image_url', 'effective_image_url',
              'video_id', 'object_story_spec',
            ].join(','),
            limit: '200',
            after: nextCursor,
          });
          const nextData = await safeJsonParse(nextResp);
          if (nextResp.ok && nextData.data) {
            for (const creative of nextData.data) {
              creativeMap.set(String(creative.id), creative);
            }
            nextCursor = nextData.paging?.cursors?.after;
            pageNum++;
          } else {
            break;
          }
        }
      }
      console.log(`[Meta Ads] Step 2: Fetched ${creativeMap.size} creatives via batch endpoint`);
    } catch (err) {
      console.warn('[Meta Ads] Step 2: Batch creatives fetch failed, will fall back to individual lookups:', err);
    }

    // Step 2b: For any ads whose creative ID wasn't found in the batch, try individual fetch
    const missingCreativeIds: string[] = [];
    for (const ad of rawAds) {
      const cid = ad.creative?.id ? String(ad.creative.id) : null;
      if (cid && !creativeMap.has(cid)) {
        missingCreativeIds.push(cid);
      }
    }

    if (missingCreativeIds.length > 0) {
      console.log(`[Meta Ads] Step 2b: ${missingCreativeIds.length} creatives not in batch, fetching individually...`);
      const uniqueMissing = [...new Set(missingCreativeIds)];
      const chunkSize = 10;
      for (let i = 0; i < uniqueMissing.length; i += chunkSize) {
        const chunk = uniqueMissing.slice(i, i + chunkSize);
        await Promise.allSettled(
          chunk.map(async (cid) => {
            try {
              const resp = await metaAdsFetch(cid, {
                fields: 'id,name,body,title,link_url,call_to_action_type,thumbnail_url,image_url,effective_image_url,video_id,object_story_spec',
              });
              const data = await safeJsonParse(resp);
              if (resp.ok && data.id) {
                creativeMap.set(String(data.id), data);
              }
            } catch {
              // Non-critical — this ad just won't have creative data
            }
          })
        );
      }
      console.log(`[Meta Ads] Step 2b: Creative map now has ${creativeMap.size} entries`);
    }

    // Step 3: Fetch ad-level insights for metrics
    const insights = await fetchAdLevelInsights(dateRange);
    const insightsMap = new Map<string, AdLevelInsight>();
    for (const insight of insights) {
      insightsMap.set(insight.ad_id, insight);
    }
    console.log(`[Meta Ads] Step 3: Fetched ${insights.length} ad-level insights`);

    // Step 4: Collect video IDs and batch-fetch video sources
    const videoIds = new Set<string>();
    for (const ad of rawAds) {
      const cid = ad.creative?.id ? String(ad.creative.id) : null;
      const creative = cid ? creativeMap.get(cid) : null;
      if (creative) {
        const videoId = creative.video_id
          || creative.object_story_spec?.video_data?.video_id
          || null;
        if (videoId) {
          videoIds.add(String(videoId));
        }
      }
    }

    const videoSourceMap = new Map<string, { source: string | null; picture: string | null }>();

    // Try batch fetch via ad account advideos endpoint
    if (videoIds.size > 0) {
      console.log(`[Meta Ads] Step 4: Fetching ${videoIds.size} video sources...`);

      // Try batch via advideos endpoint first
      try {
        const videosResponse = await metaAdsFetch(`act_${adAccountId}/advideos`, {
          fields: 'id,source,picture,thumbnails',
          limit: '200',
        });
        const videosData = await safeJsonParse(videosResponse);
        if (videosResponse.ok && videosData.data) {
          for (const video of videosData.data) {
            if (videoIds.has(String(video.id))) {
              let bestThumb = video.picture || null;
              if (video.thumbnails?.data?.length) {
                const sorted = [...video.thumbnails.data].sort((a: any, b: any) => (b.height || 0) - (a.height || 0));
                bestThumb = sorted[0]?.uri || bestThumb;
              }
              videoSourceMap.set(String(video.id), {
                source: video.source || null,
                picture: bestThumb,
              });
            }
          }
          console.log(`[Meta Ads] Step 4: Got ${videoSourceMap.size} video sources from batch endpoint`);
        }
      } catch {
        console.warn('[Meta Ads] Step 4: Batch advideos fetch failed, trying individual lookups...');
      }

      // Individual fallback for any videos not found in batch
      const missingVideoIds = Array.from(videoIds).filter(vid => !videoSourceMap.has(vid));
      if (missingVideoIds.length > 0) {
        console.log(`[Meta Ads] Step 4b: ${missingVideoIds.length} videos not in batch, fetching individually...`);
        const chunkSize = 10;
        for (let i = 0; i < missingVideoIds.length; i += chunkSize) {
          const chunk = missingVideoIds.slice(i, i + chunkSize);
          await Promise.allSettled(
            chunk.map(async (vid) => {
              try {
                const resp = await metaAdsFetch(vid, {
                  fields: 'id,source,picture,thumbnails',
                });
                const data = await safeJsonParse(resp);
                if (resp.ok) {
                  let bestThumb = data.picture || null;
                  if (data.thumbnails?.data?.length) {
                    const sorted = [...data.thumbnails.data].sort((a: any, b: any) => (b.height || 0) - (a.height || 0));
                    bestThumb = sorted[0]?.uri || bestThumb;
                  }
                  videoSourceMap.set(vid, {
                    source: data.source || null,
                    picture: bestThumb,
                  });
                }
              } catch {
                // Non-critical
              }
            })
          );
        }
        console.log(`[Meta Ads] Step 4b: Video map now has ${videoSourceMap.size} entries`);
      }
    }

    // Step 5: Merge everything together
    console.log(`[Meta Ads] Step 5: Merging ${rawAds.length} ads with ${creativeMap.size} creatives, ${videoSourceMap.size} videos, ${insightsMap.size} insights`);

    return rawAds.map((ad: any) => {
      const insight = insightsMap.get(ad.id);
      const cid = ad.creative?.id ? String(ad.creative.id) : null;
      const creative = cid ? (creativeMap.get(cid) || {}) : {};
      const adset = ad.adset || {};
      const campaign = ad.campaign || {};
      const targeting = adset.targeting || {};

      // Extract geo locations
      const geoLocations: string[] = [];
      const geo = targeting.geo_locations || {};
      if (geo.countries) {
        geoLocations.push(...(geo.countries as string[]));
      }
      if (geo.cities) {
        for (const city of geo.cities as any[]) {
          geoLocations.push(city.name || city.key || '');
        }
      }
      if (geo.regions) {
        for (const region of geo.regions as any[]) {
          geoLocations.push(region.name || region.key || '');
        }
      }

      // Extract targeting interests/tags
      const targetingTags: string[] = [];
      if (targeting.flexible_spec) {
        for (const spec of targeting.flexible_spec as any[]) {
          if (spec.interests) {
            for (const interest of spec.interests as any[]) {
              targetingTags.push(interest.name || '');
            }
          }
          if (spec.behaviors) {
            for (const behavior of spec.behaviors as any[]) {
              targetingTags.push(behavior.name || '');
            }
          }
        }
      }

      // Determine video ID
      const videoId = creative.video_id
        || creative.object_story_spec?.video_data?.video_id
        || null;
      const hasVideoData = Boolean(videoId) || Boolean(creative.object_story_spec?.video_data);
      const isVideo = hasVideoData;

      // Get video source + thumbnail
      const videoInfo = videoId ? videoSourceMap.get(String(videoId)) : null;
      const videoSource = videoInfo?.source || null;
      const videoThumbnail = videoInfo?.picture || null;

      // Build the Facebook watch URL for video ads
      let videoUrl: string | null = null;
      if (videoId) {
        videoUrl = `https://www.facebook.com/watch/?v=${videoId}`;
      }

      // Build the best image URL — prioritize full-resolution sources
      let imageUrl: string | null = null;

      // 1. effective_image_url from batch creative fetch (best quality for all ad types)
      if (creative.effective_image_url) {
        imageUrl = creative.effective_image_url;
      }

      // 2. Video thumbnail from video endpoint (high-res for video ads)
      if (!imageUrl && isVideo && videoThumbnail) {
        imageUrl = videoThumbnail;
      }

      // 3. image_url from creative
      if (!imageUrl && creative.image_url) {
        imageUrl = creative.image_url;
      }

      // 4. Dig into object_story_spec for images
      if (creative.object_story_spec) {
        const spec = creative.object_story_spec;
        if (!imageUrl && spec.link_data?.picture) {
          imageUrl = spec.link_data.picture;
        }
        if (!imageUrl && spec.video_data?.image_url) {
          imageUrl = spec.video_data.image_url;
        }
        if (!imageUrl && spec.photo_data?.url) {
          imageUrl = spec.photo_data.url;
        }
      }

      // 5. thumbnail_url (last resort — often low-res 64x64)
      if (!imageUrl) {
        imageUrl = creative.thumbnail_url || null;
      }

      // Map status
      const statusMap: Record<string, string> = {
        ACTIVE: 'active',
        PAUSED: 'paused',
        DELETED: 'deleted',
        ARCHIVED: 'archived',
      };

      return {
        ad_id: ad.id,
        ad_name: ad.name || '',
        campaign_name: campaign.name || '',
        campaign_id: campaign.id || '',
        adset_name: adset.name || '',
        status: statusMap[ad.status] || ad.status?.toLowerCase() || 'unknown',
        thumbnail_url: creative.thumbnail_url || null,
        image_url: imageUrl,
        video_url: videoUrl,
        video_source: videoSource,
        video_thumbnail: videoThumbnail || imageUrl,
        body: creative.body || null,
        title: creative.title || null,
        link_url: creative.link_url || null,
        call_to_action_type: creative.call_to_action_type || null,
        is_video: isVideo,
        age_min: targeting.age_min || null,
        age_max: targeting.age_max || null,
        geo_locations: geoLocations.filter(Boolean),
        targeting_tags: targetingTags.filter(Boolean),
        impressions: insight?.impressions || 0,
        clicks: insight?.clicks || 0,
        spend: insight?.spend || 0,
        leads: insight?.leads || 0,
        cpl: insight?.cpl || 0,
        ctr: insight?.ctr || 0,
      };
    });
  } catch (error) {
    console.error('[Meta Ads] Error fetching ads with creatives:', error);
    throw error;
  }
};
